// Global location data for worldwide operation
export interface Country {
    code: string;
    name: string;
    currency: string;
    phonePrefix: string;
    locale: string;
    flag: string;
}

export interface Currency {
    code: string;
    symbol: string;
    name: string;
}

// Major world currencies
export const SUPPORTED_CURRENCIES: Currency[] = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
    { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
    { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi' },
    { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
    { code: 'EGP', symbol: '£', name: 'Egyptian Pound' },
    { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
    { code: 'PLN', symbol: 'zł', name: 'Polish Złoty' },
    { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
    { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
    { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
    { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
    { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna' },
    { code: 'RSD', symbol: 'дин', name: 'Serbian Dinar' },
    { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
    { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
    { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal' },
    { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
    { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
    { code: 'OMR', symbol: '﷼', name: 'Omani Rial' },
    { code: 'JOD', symbol: 'د.ا', name: 'Jordanian Dinar' },
    { code: 'LBP', symbol: '£', name: 'Lebanese Pound' },
];

// Major countries for global operation
export const SUPPORTED_COUNTRIES: Country[] = [
    // North America
    { code: 'US', name: 'United States', currency: 'USD', phonePrefix: '+1', locale: 'en-US', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', currency: 'CAD', phonePrefix: '+1', locale: 'en-CA', flag: '🇨🇦' },
    { code: 'MX', name: 'Mexico', currency: 'MXN', phonePrefix: '+52', locale: 'es-MX', flag: '🇲🇽' },

    // Europe
    { code: 'GB', name: 'United Kingdom', currency: 'GBP', phonePrefix: '+44', locale: 'en-GB', flag: '🇬🇧' },
    { code: 'DE', name: 'Germany', currency: 'EUR', phonePrefix: '+49', locale: 'de-DE', flag: '🇩🇪' },
    { code: 'FR', name: 'France', currency: 'EUR', phonePrefix: '+33', locale: 'fr-FR', flag: '🇫🇷' },
    { code: 'IT', name: 'Italy', currency: 'EUR', phonePrefix: '+39', locale: 'it-IT', flag: '🇮🇹' },
    { code: 'ES', name: 'Spain', currency: 'EUR', phonePrefix: '+34', locale: 'es-ES', flag: '🇪🇸' },
    { code: 'NL', name: 'Netherlands', currency: 'EUR', phonePrefix: '+31', locale: 'nl-NL', flag: '🇳🇱' },
    { code: 'CH', name: 'Switzerland', currency: 'CHF', phonePrefix: '+41', locale: 'de-CH', flag: '🇨🇭' },
    { code: 'AT', name: 'Austria', currency: 'EUR', phonePrefix: '+43', locale: 'de-AT', flag: '🇦🇹' },
    { code: 'BE', name: 'Belgium', currency: 'EUR', phonePrefix: '+32', locale: 'fr-BE', flag: '🇧🇪' },
    { code: 'SE', name: 'Sweden', currency: 'SEK', phonePrefix: '+46', locale: 'sv-SE', flag: '🇸🇪' },
    { code: 'NO', name: 'Norway', currency: 'NOK', phonePrefix: '+47', locale: 'no-NO', flag: '🇳🇴' },
    { code: 'DK', name: 'Denmark', currency: 'DKK', phonePrefix: '+45', locale: 'da-DK', flag: '🇩🇰' },
    { code: 'FI', name: 'Finland', currency: 'EUR', phonePrefix: '+358', locale: 'fi-FI', flag: '🇫🇮' },
    { code: 'PL', name: 'Poland', currency: 'PLN', phonePrefix: '+48', locale: 'pl-PL', flag: '🇵🇱' },
    { code: 'CZ', name: 'Czech Republic', currency: 'CZK', phonePrefix: '+420', locale: 'cs-CZ', flag: '🇨🇿' },
    { code: 'HU', name: 'Hungary', currency: 'HUF', phonePrefix: '+36', locale: 'hu-HU', flag: '🇭🇺' },
    { code: 'RO', name: 'Romania', currency: 'RON', phonePrefix: '+40', locale: 'ro-RO', flag: '🇷🇴' },
    { code: 'BG', name: 'Bulgaria', currency: 'BGN', phonePrefix: '+359', locale: 'bg-BG', flag: '🇧🇬' },
    { code: 'HR', name: 'Croatia', currency: 'HRK', phonePrefix: '+385', locale: 'hr-HR', flag: '🇭🇷' },
    { code: 'RS', name: 'Serbia', currency: 'RSD', phonePrefix: '+381', locale: 'sr-RS', flag: '🇷🇸' },
    { code: 'UA', name: 'Ukraine', currency: 'UAH', phonePrefix: '+380', locale: 'uk-UA', flag: '🇺🇦' },
    { code: 'RU', name: 'Russia', currency: 'RUB', phonePrefix: '+7', locale: 'ru-RU', flag: '🇷🇺' },
    { code: 'TR', name: 'Turkey', currency: 'TRY', phonePrefix: '+90', locale: 'tr-TR', flag: '🇹🇷' },

    // Asia
    { code: 'JP', name: 'Japan', currency: 'JPY', phonePrefix: '+81', locale: 'ja-JP', flag: '🇯🇵' },
    { code: 'CN', name: 'China', currency: 'CNY', phonePrefix: '+86', locale: 'zh-CN', flag: '🇨🇳' },
    { code: 'IN', name: 'India', currency: 'INR', phonePrefix: '+91', locale: 'en-IN', flag: '🇮🇳' },
    { code: 'KR', name: 'South Korea', currency: 'KRW', phonePrefix: '+82', locale: 'ko-KR', flag: '🇰🇷' },
    { code: 'SG', name: 'Singapore', currency: 'SGD', phonePrefix: '+65', locale: 'en-SG', flag: '🇸🇬' },
    { code: 'HK', name: 'Hong Kong', currency: 'HKD', phonePrefix: '+852', locale: 'zh-HK', flag: '🇭🇰' },
    { code: 'TW', name: 'Taiwan', currency: 'TWD', phonePrefix: '+886', locale: 'zh-TW', flag: '🇹🇼' },
    { code: 'MY', name: 'Malaysia', currency: 'MYR', phonePrefix: '+60', locale: 'ms-MY', flag: '🇲🇾' },
    { code: 'TH', name: 'Thailand', currency: 'THB', phonePrefix: '+66', locale: 'th-TH', flag: '🇹🇭' },
    { code: 'VN', name: 'Vietnam', currency: 'VND', phonePrefix: '+84', locale: 'vi-VN', flag: '🇻🇳' },
    { code: 'PH', name: 'Philippines', currency: 'PHP', phonePrefix: '+63', locale: 'en-PH', flag: '🇵🇭' },
    { code: 'ID', name: 'Indonesia', currency: 'IDR', phonePrefix: '+62', locale: 'id-ID', flag: '🇮🇩' },

    // Middle East
    { code: 'IL', name: 'Israel', currency: 'ILS', phonePrefix: '+972', locale: 'he-IL', flag: '🇮🇱' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'AED', phonePrefix: '+971', locale: 'ar-AE', flag: '🇦🇪' },
    { code: 'SA', name: 'Saudi Arabia', currency: 'SAR', phonePrefix: '+966', locale: 'ar-SA', flag: '🇸🇦' },
    { code: 'QA', name: 'Qatar', currency: 'QAR', phonePrefix: '+974', locale: 'ar-QA', flag: '🇶🇦' },
    { code: 'KW', name: 'Kuwait', currency: 'KWD', phonePrefix: '+965', locale: 'ar-KW', flag: '🇰🇼' },
    { code: 'BH', name: 'Bahrain', currency: 'BHD', phonePrefix: '+973', locale: 'ar-BH', flag: '🇧🇭' },
    { code: 'OM', name: 'Oman', currency: 'OMR', phonePrefix: '+968', locale: 'ar-OM', flag: '🇴🇲' },
    { code: 'JO', name: 'Jordan', currency: 'JOD', phonePrefix: '+962', locale: 'ar-JO', flag: '🇯🇴' },
    { code: 'LB', name: 'Lebanon', currency: 'LBP', phonePrefix: '+961', locale: 'ar-LB', flag: '🇱🇧' },

    // Africa
    { code: 'ZA', name: 'South Africa', currency: 'ZAR', phonePrefix: '+27', locale: 'en-ZA', flag: '🇿🇦' },
    { code: 'NG', name: 'Nigeria', currency: 'NGN', phonePrefix: '+234', locale: 'en-NG', flag: '🇳🇬' },
    { code: 'GH', name: 'Ghana', currency: 'GHS', phonePrefix: '+233', locale: 'en-GH', flag: '🇬🇭' },
    { code: 'KE', name: 'Kenya', currency: 'KES', phonePrefix: '+254', locale: 'en-KE', flag: '🇰🇪' },
    { code: 'UG', name: 'Uganda', currency: 'UGX', phonePrefix: '+256', locale: 'en-UG', flag: '🇺🇬' },
    { code: 'TZ', name: 'Tanzania', currency: 'TZS', phonePrefix: '+255', locale: 'sw-TZ', flag: '🇹🇿' },
    { code: 'EG', name: 'Egypt', currency: 'EGP', phonePrefix: '+20', locale: 'ar-EG', flag: '🇪🇬' },
    { code: 'MA', name: 'Morocco', currency: 'MAD', phonePrefix: '+212', locale: 'ar-MA', flag: '🇲🇦' },
    { code: 'TN', name: 'Tunisia', currency: 'TND', phonePrefix: '+216', locale: 'ar-TN', flag: '🇹🇳' },

    // Oceania
    { code: 'AU', name: 'Australia', currency: 'AUD', phonePrefix: '+61', locale: 'en-AU', flag: '🇦🇺' },
    { code: 'NZ', name: 'New Zealand', currency: 'NZD', phonePrefix: '+64', locale: 'en-NZ', flag: '🇳🇿' },

    // South America
    { code: 'BR', name: 'Brazil', currency: 'BRL', phonePrefix: '+55', locale: 'pt-BR', flag: '🇧🇷' },
    { code: 'AR', name: 'Argentina', currency: 'ARS', phonePrefix: '+54', locale: 'es-AR', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', currency: 'CLP', phonePrefix: '+56', locale: 'es-CL', flag: '🇨🇱' },
    { code: 'CO', name: 'Colombia', currency: 'COP', phonePrefix: '+57', locale: 'es-CO', flag: '🇨🇴' },
    { code: 'PE', name: 'Peru', currency: 'PEN', phonePrefix: '+51', locale: 'es-PE', flag: '🇵🇪' },
    { code: 'UY', name: 'Uruguay', currency: 'UYU', phonePrefix: '+598', locale: 'es-UY', flag: '🇺🇾' },
];

// Helper functions
export const getCurrencyByCode = (code: string): Currency | undefined => {
    return SUPPORTED_CURRENCIES.find(currency => currency.code === code);
};

export const getCountryByCode = (code: string): Country | undefined => {
    return SUPPORTED_COUNTRIES.find(country => country.code === code);
};

export const getCurrencySymbol = (code: string): string => {
    const currency = getCurrencyByCode(code);
    return currency?.symbol || code;
};

export const getCountriesByCurrency = (currencyCode: string): Country[] => {
    return SUPPORTED_COUNTRIES.filter(country => country.currency === currencyCode);
};

export const getDefaultCountry = (): Country => {
    return SUPPORTED_COUNTRIES[0]; // United States as default
};

export const getDefaultCurrency = (): Currency => {
    return SUPPORTED_CURRENCIES[0]; // USD as default
};

// Popular regions grouping
export const REGIONS = {
    NORTH_AMERICA: ['US', 'CA', 'MX'],
    EUROPE: ['GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'CH', 'AT', 'BE', 'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'HU', 'RO', 'BG', 'HR', 'RS', 'UA', 'RU', 'TR'],
    ASIA: ['JP', 'CN', 'IN', 'KR', 'SG', 'HK', 'TW', 'MY', 'TH', 'VN', 'PH', 'ID'],
    MIDDLE_EAST: ['IL', 'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'JO', 'LB'],
    AFRICA: ['ZA', 'NG', 'GH', 'KE', 'UG', 'TZ', 'EG', 'MA', 'TN'],
    OCEANIA: ['AU', 'NZ'],
    SOUTH_AMERICA: ['BR', 'AR', 'CL', 'CO', 'PE', 'UY']
};